import { BookOpen, Shield, ExternalLink, AlignJustify } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CharteSignatureForm } from "@/components/home/CharteSignatureForm";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const CharteRepublicaine = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  return <div className="min-h-screen bg-background">
      <Header />
      <div className="py-8 mt-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8 pt-4">
            <AlignJustify className="w-8 h-8 text-title" />
            <h2 className="text-2xl sm:text-3xl font-bold break-words">La Charte des droits et des devoirs du citoyen français</h2>
          </div>
          
          <div className="flex flex-col md:flex-row gap-6 mb-6">
            <div className="w-full md:w-2/3 space-y-5">
              <h4 className="text-xl font-semibold text-subtitle">Qu'est-ce que la Charte des droits et des devoirs du citoyen français ?</h4>
              <p className="text-base">
                La Charte des droits et des devoirs du citoyen français est prévue à l'article 21-24 du Code civil. Elle a été introduite par la loi n°2011-672 du 16 juin 2011 relative à l'immigration, à l'intégration et à la nationalité, dans le but de rappeler aux nouveaux citoyens français les principes fondamentaux de la République et les engagements liés à l'acquisition de la nationalité française.
              </p>

              <Card className="border-none shadow-md">
                <CardContent className="p-5">
                  <p className="font-semibold mb-3">La Charte est un document pédagogique et pratique qui se compose de deux parties principales :</p>
                  
                  <div className="grid gap-5 md:grid-cols-2">
                    <div>
                      <h5 className="font-semibold flex items-center gap-2 text-subtitle mb-3">
                        <Shield className="w-5 h-5 text-title" />
                        1. Les droits du citoyen français :
                      </h5>
                      <ul className="list-disc ml-6 space-y-1.5">
                        <li>Libertés fondamentales : liberté d'expression, de culte, de réunion.</li>
                        <li>Égalité devant la loi : indépendamment de l'origine, du genre ou des croyances.</li>
                        <li>Participation à la vie démocratique : droit de vote et éligibilité.</li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-semibold flex items-center gap-2 text-subtitle mb-3">
                        <Shield className="w-5 h-5 text-title" />
                        2. Les devoirs du citoyen français :
                      </h5>
                      <ul className="list-disc ml-6 space-y-1.5">
                        <li>Respect des lois : y compris la laïcité, principe fondamental de la République.</li>
                        <li>Contribution à la société : paiement des impôts, solidarité et service national universel.</li>
                        <li>Respect des valeurs républicaines : liberté, égalité, fraternité et respect des autres.</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-primary/5 shadow-sm border-none">
                <CardContent className="p-5">
                  <p className="text-base">
                    La Charte des droits et des devoirs du citoyen français est peu connue du grand public puisque son impact est limité aux personnes naturalisées ou devenant françaises par déclaration (par mariage, par exemple), qui la signent dans des contextes spécifiques (cérémonies de naturalisation).
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-primary/5 shadow-sm border-none">
                <CardContent className="p-5">
                  <p className="font-semibold mb-3 text-subtitle">Pourtant son contenu universel et pédagogique mériterait une diffusion plus large. Elle doit en effet :</p>
                  <ul className="list-disc ml-8 space-y-2">
                    <li>Rappeler les droits et devoirs de tout citoyen français : elle constitue un texte clair et pédagogique qui pourrait renforcer la compréhension des responsabilités citoyennes.</li>
                    <li>Symboliser l'engagement républicain : elle rappelle les principes fondamentaux qui lient chaque citoyen français à la République.</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            
            <div className="w-full md:w-1/3 space-y-4">
              <Card className="border-2 border-title shadow-lg">
                <CardContent className="p-5">
                  <h3 className="text-xl font-semibold mb-3">Document Officiel</h3>
                  <p className="mb-4">Consultez la version officielle de la Charte des droits et des devoirs du citoyen français.</p>
                  <a href="/Charte_des_droits_et_devoirs_du_citoyen_francais-juin-2020.pdf" className="inline-flex items-center justify-center gap-2 w-full bg-title text-white py-2.5 px-4 rounded-lg hover:bg-title/90 transition-colors font-semibold" target="_blank" rel="noopener noreferrer">
                    Accéder à la charte
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </CardContent>
              </Card>

              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Card className="bg-[#FFFF4D] border-2 border-[#FFFF4D] shadow-lg cursor-pointer hover:bg-[#FFFF4D]/90 transition-colors">
                    <CardContent className="p-5">
                      <h3 className="text-xl font-semibold mb-3 text-[#00123A]">Signez la charte</h3>
                      <p className="text-[#00123A] mb-4">Soutenez l'action des Enfants de la République en signant La Charte. Vous recevrez gratuitement votre charte "Enfants de la République" personnalisée et numérotée !</p>
                      <p className="text-[#00123A] mb-4">
                    </p>
                      <button className="w-full bg-secondary text-white px-4 py-2.5 rounded-lg hover:bg-secondary/90 transition-colors font-semibold">
                        Je signe la charte
                      </button>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Signer la charte républicaine</DialogTitle>
                  </DialogHeader>
                  <CharteSignatureForm onSuccess={() => setIsDialogOpen(false)} />
                </DialogContent>
              </Dialog>

              <img src="/lovable-uploads/40222de6-12ea-4357-a942-a7fe55d59af0.png" alt="Liberté, égalité, fraternité, laïcité" className="rounded-lg shadow-lg w-full h-auto object-cover" />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>;
};
export default CharteRepublicaine;
